import { List } from 'immutable'
import _get from 'lodash/get'
import { get as getConfig } from 'config'
import {
  client as apolloClient,
  APOLLO_CLIENT_ERROR_POLICY_ALL,
  APOLLO_CLIENT_FETCH_POLICY_NETWORK_ONLY,
} from 'services/apollo-graphql'
import { getAuthIsLoggedIn } from 'services/auth'
import { getPrimary as getPrimaryLanguage } from 'services/languages'
import { log as logError } from 'log/error'
import { SET_AUTH_DATA } from 'services/auth/actions'
import { USER_PROFILES_SET } from 'services/user-profiles/actions'
import {
  GET_CURRENT_PROFILE,
  GET_PROFILE_BY_UID,
  GET_PROFILE_PICTURE_BY_UID,
  UPDATE_PROFILE,
  CREATE_FOLLOW,
  DELETE_FOLLOW,
  GET_FOLLOWERS,
  GET_FOLLOWING,
  GET_USER_TAG_OPTIONS,
  GET_MY_PLAYLISTS,
  GET_PUBLIC_PLAYLIST_ITEMS,
  UPDATE_PLAYLIST_VISIBILITY,
  SAVE_SHARED_PLAYLISTS,
  GET_COMMENT_COUNT,
  CREATE_PROFILE_PICTURE_UPLOAD,
  CREATE_PROFILE_PICTURE_CONFIRM,
  DELETE_PROFILE_PICTURE,
} from './query'
import * as actions from './actions'

const watcherConfig = getConfig()
const communityProfileEnabled = !!(watcherConfig && watcherConfig.features
  && watcherConfig.features.communityProfile)

const LOG_PREFIX = '[CommunityProfile]'

/**
 * Log whether an endpoint returned LIVE data or an ERROR.
 * Only logs in the browser so the dev console stays informative
 * while the server log stays clean.
 */
function logDataSource (endpoint, source, payload) {
  if (typeof window !== 'undefined') {
    let style = 'color: orange; font-weight: bold'
    if (source === 'LIVE') {
      style = 'color: green; font-weight: bold'
    } else if (source === 'ERROR') {
      style = 'color: red; font-weight: bold'
    }
    console.groupCollapsed( // eslint-disable-line no-console
      `%c${LOG_PREFIX} ${endpoint}: ${source}`,
      style,
    )
    if (payload !== undefined) {
      console.log('Payload:', payload) // eslint-disable-line no-console
    }
    console.groupEnd() // eslint-disable-line no-console
  }
}

function getLanguage (state) {
  const { user } = state
  return getPrimaryLanguage(user.getIn(['data', 'language'], List()))
}

function getAuthToken (state) {
  return state.auth.get('jwt')
}

/**
 * Extract topGenres and inProgress from a profile response and dispatch
 * them into the existing Redux stores so components don't need to change.
 */
function dispatchInlineProfileData (dispatch, profileData) {
  if (!profileData) return

  const topGenres = profileData.topGenres || []
  const watchingTags = topGenres.map((g, idx) => {
    // Strip number prefix and trailing " RC" suffix
    // e.g. "5 - Expanded Consciousness" → "Expanded Consciousness"
    // e.g. "7 - Supernatural RC" → "Supernatural"
    const cleanName = (g.name || '')
      .replace(/^\d+\s*[-–]\s*/, '')
      .replace(/\s+RC$/i, '')
      .trim()
    return {
      id: `genre-${idx}`,
      name: cleanName,
      weight: g.weight,
      viewCount: g.viewCount,
    }
  })
  dispatch(actions.setWatchingTags(watchingTags))

  const rawInProgress = profileData.inProgress || []
  const inProgress = rawInProgress
    .filter((item) => {
      if (!item || !item.media) return false
      if (item.hiddenByUser) return false
      if (item.hiddenFromContinue) return false
      return true
    })
    .map((item) => {
      const featured = _get(item, 'media.contentFeatured[0]', null)
      if (!featured) return null

      const duration = _get(item, 'media.duration', 0)
      const position = Number(item.position) || 0
      const percentComplete = duration > 0
        ? Math.round((position / duration) * 100)
        : 0
      const title = featured.title || ''
      const path = featured.path || ''
      const contentId = featured.contentId || null

      const assetUrl = _get(
        featured,
        'contentAssetReferences[0].asset.renderUrl',
        '',
      )
      const legacyUrl = _get(
        featured,
        'imageContentRelationships[0].image.url',
        '',
      )
      const imageUrl = assetUrl || legacyUrl || ''

      return {
        id: item.mediaId,
        mediaId: item.mediaId,
        title,
        path,
        contentId,
        percentComplete,
        imageUrl,
        isAssetUrl: !!assetUrl,
        duration,
        position,
      }
    })
    .filter(Boolean)
  dispatch(actions.setInProgress(inProgress))

  const sharedPlaylists = profileData.playlists || []
  if (sharedPlaylists.length > 0) {
    dispatch(actions.enrichSharedPlaylists(sharedPlaylists))
  }
}

export function watchFetchCurrentProfile ({ after }) {
  return after(
    actions.COMMUNITY_PROFILE_FETCH_CURRENT,
    async ({ dispatch, state, action }) => {
      dispatch(actions.setCurrentProfileProcessing(true))
      try {
        const language = _get(action, ['payload', 'language'])
          || getLanguage(state)
        const authToken = getAuthToken(state)

        const { data, error } = await apolloClient.query({
          query: GET_CURRENT_PROFILE,
          variables: { language, contentLanguage: [language] },
          fetchPolicy: APOLLO_CLIENT_FETCH_POLICY_NETWORK_ONLY,
          errorPolicy: APOLLO_CLIENT_ERROR_POLICY_ALL,
          context: { jwt: authToken },
        })

        if (error) {
          logError('Community profile fetch current GraphQL error', { error })
          logDataSource('userProfileCurrent', 'ERROR', error.message)
          dispatch(actions.setCurrentProfileError(error.message))
          return
        }

        // Auto-create profile if it doesn't exist yet
        if (!data.userProfileCurrent) {
          logDataSource('userProfileCurrent', 'LIVE (null – auto-creating)', null)
          const username = state.auth.get('username') || ''
          await apolloClient.mutate({
            mutation: UPDATE_PROFILE,
            variables: { input: { displayName: username } },
            context: { jwt: authToken },
          })
          // Re-fetch to get the full profile with federated data
          const { data: retryData } = await apolloClient.query({
            query: GET_CURRENT_PROFILE,
            variables: { language, contentLanguage: [language] },
            fetchPolicy: APOLLO_CLIENT_FETCH_POLICY_NETWORK_ONLY,
            errorPolicy: APOLLO_CLIENT_ERROR_POLICY_ALL,
            context: { jwt: authToken },
          })
          logDataSource('userProfileCurrent', 'LIVE (auto-created)', retryData.userProfileCurrent)
          dispatch(actions.setCurrentProfile(retryData.userProfileCurrent))
          dispatchInlineProfileData(dispatch, retryData.userProfileCurrent)
          return
        }

        logDataSource('userProfileCurrent', 'LIVE', data.userProfileCurrent)
        dispatch(actions.setCurrentProfile(data.userProfileCurrent))
        dispatchInlineProfileData(dispatch, data.userProfileCurrent)
      } catch (error) {
        logError('Community profile fetch current failed', { error })
        logDataSource('userProfileCurrent', 'ERROR', error.message)
        dispatch(actions.setCurrentProfileError(error.message))
      } finally {
        dispatch(actions.setCurrentProfileProcessing(false))
      }
    },
  )
    .when(({ state }) => getAuthIsLoggedIn(state.auth))
}

export function watchFetchMemberProfile ({ after }) {
  return after(
    actions.COMMUNITY_PROFILE_FETCH_MEMBER,
    async ({ dispatch, state, action }) => {
      dispatch(actions.setMemberProfileProcessing(true))
      try {
        const { uid } = action.payload
        const language = _get(action, ['payload', 'language'])
          || getLanguage(state)
        const authToken = getAuthToken(state)

        const { data, error } = await apolloClient.query({
          query: GET_PROFILE_BY_UID,
          variables: { uid, language, contentLanguage: [language] },
          fetchPolicy: APOLLO_CLIENT_FETCH_POLICY_NETWORK_ONLY,
          errorPolicy: APOLLO_CLIENT_ERROR_POLICY_ALL,
          context: { jwt: authToken },
        })

        if (error) {
          logError('Community profile fetch member GraphQL error', { error })
          logDataSource('userProfileByUid', 'ERROR', error.message)
          dispatch(actions.setMemberProfileError(error.message))
          return
        }

        logDataSource('userProfileByUid', 'LIVE', data.userProfileByUid)
        dispatch(actions.setMemberProfile(data.userProfileByUid))
        dispatchInlineProfileData(dispatch, data.userProfileByUid)
      } catch (error) {
        logError('Community profile fetch member failed', { error })
        logDataSource('userProfileByUid', 'ERROR', error.message)
        dispatch(actions.setMemberProfileError(error.message))
      } finally {
        dispatch(actions.setMemberProfileProcessing(false))
      }
    },
  )
}

export function watchUpdateProfile ({ after }) {
  return after(
    actions.COMMUNITY_PROFILE_UPDATE,
    async ({ dispatch, state, action }) => {
      dispatch(actions.setUpdateProcessing(true))
      try {
        const { input } = action.payload
        const authToken = getAuthToken(state)

        const { data } = await apolloClient.mutate({
          mutation: UPDATE_PROFILE,
          variables: { input },
          context: { jwt: authToken },
        })
        logDataSource('userProfileUpdate', 'LIVE', data.userProfileUpdate.profile)
        dispatch(actions.setUpdateSuccess(data.userProfileUpdate.profile))
      } catch (error) {
        logError('Community profile update failed', { error })
        logDataSource('userProfileUpdate', 'ERROR', error.message)
        dispatch(actions.setUpdateError(error.message))
      } finally {
        dispatch(actions.setUpdateProcessing(false))
      }
    },
  )
    .when(({ state }) => getAuthIsLoggedIn(state.auth))
}

export function watchFollowUser ({ after }) {
  return after(
    actions.COMMUNITY_PROFILE_FOLLOW,
    async ({ dispatch, state, action }) => {
      dispatch(actions.setFollowProcessing(true))
      try {
        const { uid } = action.payload
        const authToken = getAuthToken(state)

        await apolloClient.mutate({
          mutation: CREATE_FOLLOW,
          variables: { uid },
          context: { jwt: authToken },
        })
        logDataSource('userProfileFollow', 'LIVE', { uid })
        dispatch(actions.fetchMemberProfile(uid))
      } catch (error) {
        logError('Community profile follow failed', { error })
        logDataSource('userProfileFollow', 'ERROR', error.message)
        dispatch(actions.setFollowError(error.message))
      } finally {
        dispatch(actions.setFollowProcessing(false))
      }
    },
  )
    .when(({ state }) => getAuthIsLoggedIn(state.auth))
}

export function watchUnfollowUser ({ after }) {
  return after(
    actions.COMMUNITY_PROFILE_UNFOLLOW,
    async ({ dispatch, state, action }) => {
      dispatch(actions.setFollowProcessing(true))
      try {
        const { uid } = action.payload
        const authToken = getAuthToken(state)

        await apolloClient.mutate({
          mutation: DELETE_FOLLOW,
          variables: { uid },
          context: { jwt: authToken },
        })
        logDataSource('userProfileUnfollow', 'LIVE', { uid })
        dispatch(actions.fetchMemberProfile(uid))
      } catch (error) {
        logError('Community profile unfollow failed', { error })
        logDataSource('userProfileUnfollow', 'ERROR', error.message)
        dispatch(actions.setFollowError(error.message))
      } finally {
        dispatch(actions.setFollowProcessing(false))
      }
    },
  )
    .when(({ state }) => getAuthIsLoggedIn(state.auth))
}

export function watchFetchTagOptions ({ after }) {
  return after(
    actions.COMMUNITY_PROFILE_FETCH_TAG_OPTIONS,
    async ({ dispatch, state }) => {
      dispatch(actions.setTagOptionsProcessing(true))
      try {
        const authToken = getAuthToken(state)
        const language = getLanguage(state) || 'en'

        const { data, error } = await apolloClient.query({
          query: GET_USER_TAG_OPTIONS,
          variables: { language },
          fetchPolicy: APOLLO_CLIENT_FETCH_POLICY_NETWORK_ONLY,
          errorPolicy: APOLLO_CLIENT_ERROR_POLICY_ALL,
          context: { jwt: authToken },
        })

        if (error) {
          logError('Community profile tag options GraphQL error', { error })
          logDataSource('userTagOptionsList', 'ERROR', error.message)
          dispatch(actions.setTagOptionsError(error.message))
          return
        }

        const allOptions = (data.userTagOptionsList || []).map((opt) => ({
          id: Number(opt.id),
          label: opt.label,
          tag: opt.tag,
          category: opt.category || 'topic',
        }))

        const vibes = allOptions.filter((o) => o.category === 'identity_vibe')
        const topics = allOptions.filter((o) => o.category !== 'identity_vibe')
        const tagPayload = {
          data: allOptions,
          vibes,
          topics,
        }
        logDataSource('userTagOptionsList', 'LIVE', {
          raw: data.userTagOptionsList,
          mapped: tagPayload,
        })
        dispatch(actions.setTagOptions(tagPayload))
      } catch (error) {
        logError('Community profile tag options failed', { error })
        logDataSource('userTagOptionsList', 'ERROR', error.message)
        dispatch(actions.setTagOptionsError(error.message))
      } finally {
        dispatch(actions.setTagOptionsProcessing(false))
      }
    },
  )
}

export function watchFetchFollowers ({ after }) {
  return after(
    actions.COMMUNITY_PROFILE_FETCH_FOLLOWERS,
    async ({ dispatch, state, action }) => {
      dispatch(actions.setFollowersProcessing(true))
      try {
        const { uid, first, after: afterCursor } = action.payload
        const authToken = getAuthToken(state)

        const { data, error } = await apolloClient.query({
          query: GET_FOLLOWERS,
          variables: { uid, first: first || 20, after: afterCursor },
          fetchPolicy: APOLLO_CLIENT_FETCH_POLICY_NETWORK_ONLY,
          errorPolicy: APOLLO_CLIENT_ERROR_POLICY_ALL,
          context: { jwt: authToken },
        })

        if (error) {
          logError('Community profile followers GraphQL error', { error })
          logDataSource('userProfileFollowers', 'ERROR', error.message)
          dispatch(actions.setFollowersError(error.message))
          return
        }

        logDataSource('userProfileFollowers', 'LIVE', data.userProfileFollowers)
        dispatch(actions.setFollowers(data.userProfileFollowers))
      } catch (error) {
        logError('Community profile followers failed', { error })
        logDataSource('userProfileFollowers', 'ERROR', error.message)
        dispatch(actions.setFollowersError(error.message))
      } finally {
        dispatch(actions.setFollowersProcessing(false))
      }
    },
  )
}

export function watchFetchFollowing ({ after }) {
  return after(
    actions.COMMUNITY_PROFILE_FETCH_FOLLOWING,
    async ({ dispatch, state, action }) => {
      dispatch(actions.setFollowingProcessing(true))
      try {
        const { uid, first, after: afterCursor } = action.payload
        const authToken = getAuthToken(state)

        const { data, error } = await apolloClient.query({
          query: GET_FOLLOWING,
          variables: { uid, first: first || 20, after: afterCursor },
          fetchPolicy: APOLLO_CLIENT_FETCH_POLICY_NETWORK_ONLY,
          errorPolicy: APOLLO_CLIENT_ERROR_POLICY_ALL,
          context: { jwt: authToken },
        })

        if (error) {
          logError('Community profile following GraphQL error', { error })
          logDataSource('userProfileFollowing', 'ERROR', error.message)
          dispatch(actions.setFollowingError(error.message))
          return
        }

        logDataSource('userProfileFollowing', 'LIVE', data.userProfileFollowing)
        dispatch(actions.setFollowing(data.userProfileFollowing))
      } catch (error) {
        logError('Community profile following failed', { error })
        logDataSource('userProfileFollowing', 'ERROR', error.message)
        dispatch(actions.setFollowingError(error.message))
      } finally {
        dispatch(actions.setFollowingProcessing(false))
      }
    },
  )
}

export function watchFetchPlaylists ({ after }) {
  return after(
    actions.COMMUNITY_PROFILE_FETCH_PLAYLISTS,
    async ({ dispatch, state }) => {
      dispatch(actions.setPlaylistsProcessing(true))
      try {
        const authToken = getAuthToken(state)

        const { data } = await apolloClient.query({
          query: GET_MY_PLAYLISTS,
          fetchPolicy: APOLLO_CLIENT_FETCH_POLICY_NETWORK_ONLY,
          context: { jwt: authToken },
        })
        const playlists = _get(data, 'playlistList.playlists', []) || []
        logDataSource('myPlaylists', 'LIVE', playlists)

        const SYSTEM_PLAYLIST_TYPES = ['default', 'offline', 'yoga', 'favorites']
        const userPlaylists = playlists.filter(
          (pl) => pl && pl.id
            && !SYSTEM_PLAYLIST_TYPES.includes(pl.playlistType)
            && (pl.playlistName || '').toLowerCase() !== 'default',
        )
        dispatch(actions.setPlaylists(userPlaylists))
      } catch (error) {
        logError('Community profile playlists failed', { error })
        logDataSource('myPlaylists', 'ERROR', error.message)
        dispatch(actions.setPlaylists([]))
      } finally {
        dispatch(actions.setPlaylistsProcessing(false))
      }
    },
  )
    .when(({ state }) => getAuthIsLoggedIn(state.auth))
}

export function watchSaveSharedPlaylists ({ after }) {
  return after(
    actions.COMMUNITY_PROFILE_SAVE_SHARED_PLAYLISTS,
    async ({ dispatch, state, action }) => {
      dispatch(actions.setSaveSharedPlaylistsProcessing(true))
      try {
        const { sharedPlaylistIds } = action.payload
        const authToken = getAuthToken(state)

        const { data } = await apolloClient.mutate({
          mutation: SAVE_SHARED_PLAYLISTS,
          variables: { input: { sharedPlaylistIds } },
          context: { jwt: authToken },
        })
        const updatedProfile = _get(data, 'userProfileUpdate.profile', {})
        logDataSource('saveSharedPlaylists', 'LIVE', updatedProfile)
        dispatch(actions.setCurrentProfile(updatedProfile))
        // Re-enrich playlists so carousel items load without a page refresh
        const playlists = _get(updatedProfile, 'playlists', [])
        if (playlists.length > 0) {
          dispatch(actions.enrichSharedPlaylists(playlists))
        }
      } catch (error) {
        logError('Community profile save shared playlists failed', { error })
        logDataSource('saveSharedPlaylists', 'ERROR', error.message)
        dispatch(actions.setSaveSharedPlaylistsError(error.message))
      } finally {
        dispatch(actions.setSaveSharedPlaylistsProcessing(false))
      }
    },
  )
    .when(({ state }) => getAuthIsLoggedIn(state.auth))
}

export function watchEnrichSharedPlaylists ({ after }) {
  return after(
    actions.COMMUNITY_PROFILE_ENRICH_SHARED_PLAYLISTS,
    async ({ dispatch, state, action }) => {
      try {
        const { playlists } = action.payload
        if (!playlists || playlists.length === 0) return

        const authToken = getAuthToken(state)
        const language = getLanguage(state)

        const detailsMap = {}
        await Promise.all(
          playlists.map(async (playlist) => {
            try {
              const itemsResult = await apolloClient.query({
                query: GET_PUBLIC_PLAYLIST_ITEMS,
                variables: {
                  playlistId: playlist.id,
                  playlistType: playlist.playlistType || 'user',
                  language: [language],
                  limit: 5,
                },
                fetchPolicy: APOLLO_CLIENT_FETCH_POLICY_NETWORK_ONLY,
                context: { jwt: authToken },
              })
              const items = _get(itemsResult, 'data.playlistItemList.playlistItems', []) || []
              const totalCount = _get(itemsResult, 'data.playlistItemList.count', 0)
              detailsMap[playlist.id] = {
                totalItemCount: totalCount || playlist.itemCount || 0,
                items: items.map((item) => {
                  const content = _get(item, 'content[0]', {}) || {}
                  const assetUrl = _get(
                    content,
                    'contentAssetReferences[0].asset.renderUrl',
                    '',
                  )
                  const legacyUrl = _get(
                    content,
                    'imageContentRelationships[0].image.url',
                    '',
                  )
                  const imageUrl = assetUrl || legacyUrl || ''
                  return {
                    id: item.id,
                    contentId: item.contentId,
                    contentType: item.contentType,
                    title: content.title || '',
                    path: content.path || '',
                    imageUrl,
                    isAssetUrl: !!assetUrl,
                  }
                }),
              }
            } catch (err) {
              logError('Shared playlist items fetch failed', {
                error: err,
                playlistId: playlist.id,
              })
              detailsMap[playlist.id] = {
                totalItemCount: playlist.itemCount || 0,
                items: [],
              }
            }
          }),
        )
        logDataSource('enrichSharedPlaylists', 'LIVE', detailsMap)
        dispatch(actions.setSharedPlaylistDetails(detailsMap))
      } catch (error) {
        logError('Shared playlist enrichment failed', { error })
      }
    },
  )
    .when(({ state }) => getAuthIsLoggedIn(state.auth))
}

export function watchUpdatePlaylistVisibility ({ after }) {
  return after(
    actions.COMMUNITY_PROFILE_UPDATE_PLAYLIST_VISIBILITY,
    async ({ dispatch, state, action }) => {
      try {
        const { playlistId, visibility } = action.payload
        const authToken = getAuthToken(state)

        const { data } = await apolloClient.mutate({
          mutation: UPDATE_PLAYLIST_VISIBILITY,
          variables: { playlistId, visibility },
          context: { jwt: authToken },
        })
        const updated = _get(data, 'updatePlaylistVisibility.playlist', {})
        logDataSource('updatePlaylistVisibility', 'LIVE', updated)

        // Update the picker-list copy if it exists
        dispatch({
          type: actions.SET_COMMUNITY_PROFILE_PLAYLIST_VISIBILITY,
          payload: {
            playlistId: updated.id || playlistId,
            visibility: updated.visibility || visibility,
          },
        })

        // Update the shared playlists on the current profile so the UI
        // reflects the change immediately without a full refetch.
        const currentProfile = state.communityProfile.getIn(['currentProfile', 'data'])
        if (currentProfile) {
          const profilePlaylists = currentProfile.get('playlists')
          if (profilePlaylists) {
            const idx = profilePlaylists.findIndex((p) => p.get('id') === playlistId)
            if (idx !== -1) {
              const updatedProfile = currentProfile.setIn(
                ['playlists', idx, 'visibility'],
                updated.visibility || visibility,
              )
              dispatch(actions.setCurrentProfile(updatedProfile.toJS()))
            }
          }
        }
      } catch (error) {
        logError('Community profile playlist visibility update failed', { error })
        logDataSource('updatePlaylistVisibility', 'ERROR', error.message)
      }
    },
  )
    .when(({ state }) => getAuthIsLoggedIn(state.auth))
}

// topGenres data now arrives inline with the profile query (via dispatchInlineProfileData).
// This watcher is kept so the action type dispatches without error.
export function watchFetchWatchingTags ({ after }) {
  return after(
    actions.COMMUNITY_PROFILE_FETCH_WATCHING_TAGS,
    async () => {},
  )
}

// inProgress data now arrives inline with the profile query (via dispatchInlineProfileData).
// This watcher is kept so the action type dispatches without error.
export function watchFetchInProgress ({ after }) {
  return after(
    actions.COMMUNITY_PROFILE_FETCH_IN_PROGRESS,
    async () => {},
  )
}

// myCommunitySpaces does not exist on the stage GraphQL server.
// This watcher dispatches empty data until the backend deploys the endpoint.
export function watchFetchCommunitySpaces ({ after }) {
  return after(
    actions.COMMUNITY_PROFILE_FETCH_COMMUNITY_SPACES,
    async ({ dispatch }) => {
      dispatch(actions.setCommunitySpaces([]))
    },
  )
}

export function watchFetchActivitySummary ({ after }) {
  return after(
    actions.COMMUNITY_PROFILE_FETCH_ACTIVITY_SUMMARY,
    async ({ dispatch, state }) => {
      dispatch(actions.setActivitySummaryProcessing(true))
      try {
        const authToken = getAuthToken(state)

        const commentsResult = await apolloClient.query({
          query: GET_COMMENT_COUNT,
          fetchPolicy: APOLLO_CLIENT_FETCH_POLICY_NETWORK_ONLY,
          context: { jwt: authToken },
        }).catch(() => null)

        const commentsCount = _get(commentsResult, 'data.commentList.count', 0)

        const summaryPayload = {
          comments: commentsCount,
          reactions: 0,
          // groups count unavailable until myCommunitySpaces endpoint is deployed
          groups: 0,
        }
        logDataSource('activitySummary', 'LIVE', {
          commentListRaw: commentsResult,
          result: summaryPayload,
          note: 'reactions=0 (no endpoint), groups=0 (myCommunitySpaces not deployed)',
        })
        dispatch(actions.setActivitySummary(summaryPayload))
      } catch (error) {
        logError('Community profile activity summary failed', { error })
        logDataSource('activitySummary', 'ERROR', error.message)
        dispatch(actions.setActivitySummary({ comments: 0, reactions: 0, groups: 0 }))
      } finally {
        dispatch(actions.setActivitySummaryProcessing(false))
      }
    },
  )
    .when(({ state }) => getAuthIsLoggedIn(state.auth))
}

/**
 * Upload a profile picture via a presigned S3 URL.
 * Flow: requestProfilePictureUpload -> PUT to S3 -> confirmProfilePictureUpload
 * The backend provides a single uploadUrl. The frontend handles display-time
 * resizing via imgix query parameters.
 */
export function watchUploadAvatar ({ after }) {
  return after(
    actions.COMMUNITY_PROFILE_UPLOAD_AVATAR,
    async ({ dispatch, state, action }) => {
      dispatch(actions.setAvatarUploadProcessing(true))
      try {
        const { imageBlob, fileExtension } = action.payload
        const authToken = getAuthToken(state)

        // 1. Request a presigned upload URL (network-only to avoid stale URL from cache)
        const { data } = await apolloClient.mutate({
          mutation: CREATE_PROFILE_PICTURE_UPLOAD,
          variables: { fileExtension: fileExtension || 'png' },
          context: { jwt: authToken },
          fetchPolicy: APOLLO_CLIENT_FETCH_POLICY_NETWORK_ONLY,
        })
        const uploadRequest = data.requestProfilePictureUpload
        logDataSource('requestProfilePictureUpload', 'LIVE', {
          imageUuid: uploadRequest.imageUuid,
          expiresAt: uploadRequest.expiresAt,
        })

        const CONTENT_TYPE_MAP = {
          jpg: 'image/jpeg',
          jpeg: 'image/jpeg',
          png: 'image/png',
          gif: 'image/gif',
        }
        const contentType = CONTENT_TYPE_MAP[fileExtension] || 'image/png'

        // 2. Upload the image to S3 using the URL from this response (matches imageUuid)
        const uploadResponse = await fetch(uploadRequest.uploadUrl, {
          method: 'PUT',
          body: imageBlob,
          headers: { 'Content-Type': contentType },
        })
        if (!uploadResponse.ok) {
          throw new Error(`Profile image S3 upload failed: ${uploadResponse.status}`)
        }

        // 3. Confirm the upload
        const confirmResult = await apolloClient.mutate({
          mutation: CREATE_PROFILE_PICTURE_CONFIRM,
          variables: {
            imageUuid: uploadRequest.imageUuid,
            imageExtension: fileExtension || 'png',
          },
          context: { jwt: authToken },
        })
        const confirmed = confirmResult.data.confirmProfilePictureUpload
        logDataSource('confirmProfilePictureUpload', 'LIVE', confirmed)

        dispatch(actions.setAvatarUploadSuccess())

        // Re-fetch the current profile to get updated avatar URL
        dispatch(actions.fetchCurrentProfile())
      } catch (error) {
        logError('Community profile avatar upload failed', { error })
        logDataSource('uploadAvatar', 'ERROR', error.message)
        dispatch(actions.setAvatarUploadError(error.message))
      } finally {
        dispatch(actions.setAvatarUploadProcessing(false))
      }
    },
  )
    .when(({ state }) => getAuthIsLoggedIn(state.auth))
}

/**
 * Delete the current user's profile picture via GraphQL.
 */
export function watchDeleteAvatar ({ after }) {
  return after(
    actions.COMMUNITY_PROFILE_DELETE_AVATAR,
    async ({ dispatch, state }) => {
      dispatch(actions.setAvatarDeleteProcessing(true))
      try {
        const authToken = getAuthToken(state)

        const { data } = await apolloClient.mutate({
          mutation: DELETE_PROFILE_PICTURE,
          context: { jwt: authToken },
        })
        logDataSource('deleteProfilePicture', 'LIVE', data.deleteProfilePicture)

        dispatch(actions.setAvatarDeleteSuccess())

        // Re-fetch the current profile to clear the avatar URL
        dispatch(actions.fetchCurrentProfile())
      } catch (error) {
        logError('Community profile avatar delete failed', { error })
        logDataSource('deleteProfilePicture', 'ERROR', error.message)
        dispatch(actions.setAvatarDeleteError(error.message))
      } finally {
        dispatch(actions.setAvatarDeleteProcessing(false))
      }
    },
  )
    .when(({ state }) => getAuthIsLoggedIn(state.auth))
}

/**
 * Fetch profile pictures by uid via GraphQL.
 * Skips uids already cached in profilePictures.data.
 */
export function watchFetchProfilePictures ({ after }) {
  return after(
    actions.COMMUNITY_PROFILE_FETCH_PROFILE_PICTURES,
    async ({ dispatch, state, action }) => {
      if (!communityProfileEnabled) return
      dispatch(actions.setProfilePicturesProcessing(true))
      try {
        const { uids } = action.payload
        if (!uids || uids.length === 0) return

        const cached = state.communityProfile.getIn(['profilePictures', 'data'])
        const uncached = uids.filter((uid) => !cached.has(String(uid)))
        if (uncached.length === 0) return

        const authToken = getAuthToken(state)
        const results = {}

        await Promise.all(
          uncached.map(async (uid) => {
            try {
              const { data } = await apolloClient.query({
                query: GET_PROFILE_PICTURE_BY_UID,
                variables: { uid: String(uid) },
                fetchPolicy: APOLLO_CLIENT_FETCH_POLICY_NETWORK_ONLY,
                errorPolicy: APOLLO_CLIENT_ERROR_POLICY_ALL,
                context: { jwt: authToken },
              })
              const profile = data.userProfileByUid
              if (profile && profile.profilePicture) {
                results[String(uid)] = profile.profilePicture
              }
            } catch (err) {
              logError('Profile picture fetch failed for uid', { error: err, uid })
            }
          }),
        )

        if (Object.keys(results).length > 0) {
          logDataSource('fetchProfilePictures', 'LIVE', results)
          dispatch(actions.setProfilePictures(results))
        }
      } catch (error) {
        logError('Profile pictures batch fetch failed', { error })
        dispatch(actions.setProfilePicturesError(error.message))
      } finally {
        dispatch(actions.setProfilePicturesProcessing(false))
      }
    },
  )
    .when(({ state }) => getAuthIsLoggedIn(state.auth))
}

/**
 * When user profiles are loaded (multi-profile list), automatically
 * fetch profile pictures for all uids via GraphQL.
 */
export function watchUserProfilesForPictures ({ after }) {
  return after(
    USER_PROFILES_SET,
    async ({ dispatch, state }) => {
      if (!communityProfileEnabled) return
      const profiles = state.userProfiles.get('data')
      if (!profiles || profiles.size === 0) return

      const uids = profiles
        .map((p) => p.get('uid'))
        .filter(Boolean)
        .toJS()

      if (uids.length > 0) {
        dispatch(actions.fetchProfilePictures(uids))
      }
    },
  )
    .when(({ state }) => getAuthIsLoggedIn(state.auth))
}

/**
 * When auth data is set (login / page load), fetch the current user's
 * profile picture so the nav-bar avatar renders immediately.
 */
export function watchAuthForCurrentProfilePicture ({ after }) {
  return after(
    SET_AUTH_DATA,
    async ({ dispatch, state }) => {
      if (!communityProfileEnabled) return
      const uid = state.auth.get('uid')
      if (!uid) return

      const cached = state.communityProfile.getIn(['profilePictures', 'data', String(uid)])
      if (cached && cached.size > 0) return

      dispatch(actions.fetchProfilePictures([uid]))
    },
  )
    .when(({ state }) => getAuthIsLoggedIn(state.auth))
}
